import { Link } from 'react-router';
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Car, Users, TrendingUp, Plus, Eye, Building2, Upload, ArrowUpRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getDealerOverview, DealerOverview } from '../../../services/dealer.service';
import { Seo } from '../../components/Seo';

// ---- Mock Data (for modules not yet backed by database tables) ----
const TEAM = [
  { name: 'Samir B.', role: 'Sales Rep', listings: 12, leads: 24, avatar: 'S' },
  { name: 'Nadia K.', role: 'Sales Rep', listings: 8, leads: 18, avatar: 'N' },
  { name: 'Omar F.', role: 'Manager', listings: 15, leads: 31, avatar: 'O' },
];

const BRANCHES = [
  { name: 'Casablanca Showroom', city: 'Casablanca', active: 18, staff: 5 },
  { name: 'Rabat Branch', city: 'Rabat', active: 12, staff: 3 },
];

// Default monthly sales is 0 until transaction/sales tracking is recorded
const DEFAULT_MONTHLY_SALES: Record<string, number> = {
  Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0,
  Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0,
};

const apiBaseUrl = (import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1").replace(/\/api\/v1\/?$/, "");
const imageUrl = (image: string | null) =>
  !image
    ? "https://placehold.co/160x120?text=Vehicle"
    : image.startsWith("http")
      ? image
      : `${apiBaseUrl}/volume/${image.replace(/^\/+/, "")}`;

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function DealerDashboardSkeleton() {
  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton className="h-8 w-52 mb-2" />
          <Skeleton className="h-4 w-56" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-11 w-36 rounded-xl" />
          <Skeleton className="h-11 w-36 rounded-xl" />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((card) => (
          <div key={card} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <div className="flex justify-between mb-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="h-4 w-14" />
            </div>
            <Skeleton className="h-8 w-24 mb-2" />
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <Skeleton className="h-6 w-52 mb-5" />
          <Skeleton className="h-[180px] w-full rounded-xl" />
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="flex justify-between p-5 border-b border-[#E5E7EB]">
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-4 w-16" />
          </div>
          {[1, 2].map((branch) => (
            <div key={branch} className="flex gap-3 items-center p-4 border-b border-[#F3F4F6]">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-36 mb-2" />
                <Skeleton className="h-3 w-28" />
              </div>
              <Skeleton className="h-6 w-16 rounded-lg" />
            </div>
          ))}
          <div className="p-4">
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="flex justify-between p-5 border-b border-[#E5E7EB]">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-16" />
          </div>
          {[1, 2, 3].map((member) => (
            <div key={member} className="flex items-center gap-3 p-4 border-b border-[#F3F4F6]">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-28 mb-2" />
                <Skeleton className="h-3 w-20" />
              </div>
              <div>
                <Skeleton className="h-3 w-16 mb-2" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="flex justify-between p-5 border-b border-[#E5E7EB]">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-16" />
          </div>
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex items-center gap-3 p-4 border-b border-[#F3F4F6]">
              <Skeleton className="w-16 h-12 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div>
                <Skeleton className="h-5 w-16 rounded-lg mb-2" />
                <Skeleton className="h-3 w-14" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DealerDashboard() {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<DealerOverview | null>(null);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);
        const data = await getDealerOverview();
        setOverview(data);
      } catch (err) {
        console.error("Failed to load dealer overview:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, []);

  const getRoleLabel = (role: string) => {
    const map: Record<string, string> = {
      'Sales Rep': t('dealerDashboard.team.role.salesRep'),
      'Manager': t('dealerDashboard.team.role.manager'),
    };
    return map[role] || role;
  };

  if (loading) {
    return <DealerDashboardSkeleton />;
  }

  // --- Dynamic Data with Graceful Future-Proof Fallbacks ---
  const kpis = overview?.kpis;
  const dealerName = overview?.dealer?.name || 'Karimi Motors';
  const isVerified = overview?.dealer?.isVerified ?? true;

  // 1. Total Inventory (Dynamic)
  const totalInventory = kpis ? kpis.totalInventory : 45;
  const activeInventory = kpis ? kpis.activeInventory : 38;

  // 2. Monthly Leads (Dynamic)
  const monthlyLeadsCount = kpis ? (kpis.leadsThisMonth ?? kpis.totalLeads) : 55;
  const leadsDiffPercent = kpis && kpis.leadsPreviousMonth !== undefined
    ? kpis.leadsPreviousMonth === 0
      ? kpis.leadsThisMonth > 0 ? '+100%' : '0%'
      : `${Math.round(((kpis.leadsThisMonth - kpis.leadsPreviousMonth) / kpis.leadsPreviousMonth) * 100)}%`
    : '0%';

  // 3. Sales This Month (Dynamic from backend; defaults safely to 0)
  const salesCount = kpis?.salesThisMonth !== undefined ? String(kpis.salesThisMonth) : '0';
  const salesSub = kpis?.salesRevenue ?? t('dealerDashboard.kpi.revenue', { amount: '0' });

  // 4. Profile Views (Dynamic from backend; defaults safely to 0)
  const profileViews = kpis?.profileViews !== undefined ? Number(kpis.profileViews).toLocaleString() : '0';
  const profileViewsSub = t('dealerDashboard.kpi.vsLast', { percent: '0' });

  // 5. Chart Data (Dynamic Leads + Sales)
  const chartData = (overview?.monthlyLeads && overview.monthlyLeads.length > 0)
    ? overview.monthlyLeads.map((item) => ({
        month: item.month,
        leads: item.leads,
        sales: DEFAULT_MONTHLY_SALES[item.month] ?? 0,
      }))
    : [
        { month: 'Jan', leads: 0, sales: 0 },
        { month: 'Feb', leads: 0, sales: 0 },
        { month: 'Mar', leads: 0, sales: 0 },
        { month: 'Apr', leads: 0, sales: 0 },
        { month: 'May', leads: 0, sales: 0 },
        { month: 'Jun', leads: 0, sales: 0 },
      ];

  // 6. Recent Inventory (Dynamic)
  const recentInventoryList = overview
    ? overview.recentInventory
    : [
        { id: '1', title: 'Mercedes GLE 2022', price: 720000, image: null, isActive: true, leads: 12 },
        { id: '2', title: 'BMW X5 2023', price: 850000, image: null, isActive: false, leads: 8 },
        { id: '3', title: 'Audi Q7 2022', price: 680000, image: null, isActive: true, leads: 6 },
      ];

  return (
    <>
    <Seo
      title="Dealer Dashboard | Morrocars"
      description="Manage your dealership inventory, track leads and sales, monitor vehicle listings, and view dealer performance on Morrocars."
    />
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl text-[#2D323A]">{t('dealerDashboard.title')}</h1>
          <p className="text-[#717182]">
            {t('dealerDashboard.subtitle', { dealerName })} {isVerified && `— ${t('dealerDashboard.verifiedBadge', 'Verified Dealer')}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/dealer/add-vehicle" className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] bg-white rounded-xl hover:border-[#1E6DFF] transition-colors text-sm text-[#2D323A]">
            <Upload className="w-4 h-4" /> {t('dealerDashboard.buttons.bulkUpload')}
          </Link>
          <Link to="/dealer/add-vehicle" className="flex items-center gap-2 px-4 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md text-sm">
            <Plus className="w-4 h-4" /> {t('dealerDashboard.buttons.addVehicle')}
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: t('dealerDashboard.kpi.totalInventory'),
            value: String(totalInventory),
            sub: `${activeInventory} ${t('dealerDashboard.kpi.active')}`,
            icon: Car,
            color: 'bg-[#1E6DFF]/10 text-[#1E6DFF]',
          },
          {
            label: t('dealerDashboard.kpi.monthlyLeads'),
            value: String(monthlyLeadsCount),
            sub: `${leadsDiffPercent} vs last`,
            icon: Users,
            color: 'bg-[#22C55E]/10 text-[#22C55E]',
          },
          {
            label: t('dealerDashboard.kpi.salesThisMonth'),
            value: String(salesCount),
            sub: String(salesSub),
            icon: TrendingUp,
            color: 'bg-[#F59E0B]/10 text-[#F59E0B]',
          },
          {
            label: t('dealerDashboard.kpi.profileViews'),
            value: String(profileViews),
            sub: String(profileViewsSub),
            icon: Eye,
            color: 'bg-[#1E6DFF]/10 text-[#1E6DFF]',
          },
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl text-[#2D323A]">{stat.value}</p>
              <p className="text-sm text-[#717182]">{stat.label}</p>
              <p className="text-xs text-[#22C55E] mt-1 flex items-center gap-0.5">
                <ArrowUpRight className="w-3 h-3" /> {stat.sub}
              </p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <h3 className="text-[#2D323A] mb-4">{t('dealerDashboard.charts.salesLeadsPerformance')}</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
              <Bar dataKey="leads" fill="#1E6DFF" radius={[4, 4, 0, 0]} name={t('dealerDashboard.charts.leads')} />
              <Bar dataKey="sales" fill="#22C55E" radius={[4, 4, 0, 0]} name={t('dealerDashboard.charts.sales')} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Branches (Mock) */}
        {/* <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">{t('dealerDashboard.branches.title')}</h3>
            <Link to="/dealer/branches" className="text-sm text-[#1E6DFF] hover:underline">{t('dealerDashboard.buttons.manage')}</Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {BRANCHES.map(b => (
              <div key={b.name} className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#1E6DFF]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#2D323A]">{b.name}</p>
                  <p className="text-xs text-[#717182]">{b.active} {t('dealerDashboard.branches.activeListings')} · {b.staff} {t('dealerDashboard.branches.staff')}</p>
                </div>
                <span className="text-xs px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] rounded-lg">{t('dealerDashboard.branches.status')}</span>
              </div>
            ))}
            <div className="p-4">
              <Link to="/dealer/branches" className="flex items-center gap-2 text-sm text-[#1E6DFF] hover:underline">
                <Plus className="w-4 h-4" /> {t('dealerDashboard.buttons.addBranch')}
              </Link>
            </div>
          </div>
        </div> */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">{t('dealerDashboard.inventory.title')}</h3>
            <Link to="/dealer/inventory" className="text-sm text-[#1E6DFF] hover:underline">{t('dealerDashboard.buttons.viewAll')}</Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {recentInventoryList.length === 0 ? (
              <p className="p-4 text-sm text-[#717182]">No inventory available yet.</p>
            ) : (
              recentInventoryList.map((vehicle) => (
                <div key={vehicle.id} className="flex items-center gap-3 p-4">
                  <img
                    src={imageUrl(vehicle.image)}
                    alt={vehicle.title}
                    className="w-16 h-12 rounded-xl object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#2D323A] truncate">{vehicle.title}</p>
                    <p className="text-xs text-[#717182]">
                      {vehicle.price === null || vehicle.price === undefined ? "Price on request" : `${Number(vehicle.price).toLocaleString()} MAD`}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-lg block mb-1 ${vehicle.isActive ? "bg-[#22C55E]/10 text-[#22C55E]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
                      {vehicle.isActive ? t('dealerDashboard.inventory.status.active', 'active') : 'inactive'}
                    </span>
                    <span className="text-xs text-[#717182]">{vehicle.leads ?? 0} {t('dealerDashboard.inventory.leads')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Team + Inventory */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Team Members (Mock) */}
        {/* <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">{t('dealerDashboard.team.title')}</h3>
            <Link to="/dealer/team" className="text-sm text-[#1E6DFF] hover:underline">{t('dealerDashboard.buttons.manage')}</Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {TEAM.map(m => (
              <div key={m.name} className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 bg-[#2D323A] rounded-xl flex items-center justify-center text-white font-medium">
                  {m.avatar}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#2D323A]">{m.name}</p>
                  <p className="text-xs text-[#717182]">{getRoleLabel(m.role)}</p>
                </div>
                <div className="text-right text-xs text-[#717182]">
                  <p>{m.listings} {t('dealerDashboard.team.listings')}</p>
                  <p>{m.leads} {t('dealerDashboard.team.leads')}</p>
                </div>
              </div>
            ))}
          </div>
        </div> */}

        {/* Recent Inventory (Dynamic from database) */}
        {/* <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">{t('dealerDashboard.inventory.title')}</h3>
            <Link to="/dealer/inventory" className="text-sm text-[#1E6DFF] hover:underline">{t('dealerDashboard.buttons.viewAll')}</Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {recentInventoryList.length === 0 ? (
              <p className="p-4 text-sm text-[#717182]">No inventory available yet.</p>
            ) : (
              recentInventoryList.map((vehicle) => (
                <div key={vehicle.id} className="flex items-center gap-3 p-4">
                  <img
                    src={imageUrl(vehicle.image)}
                    alt={vehicle.title}
                    className="w-16 h-12 rounded-xl object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#2D323A] truncate">{vehicle.title}</p>
                    <p className="text-xs text-[#717182]">
                      {vehicle.price === null || vehicle.price === undefined ? "Price on request" : `${Number(vehicle.price).toLocaleString()} MAD`}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-lg block mb-1 ${vehicle.isActive ? "bg-[#22C55E]/10 text-[#22C55E]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
                      {vehicle.isActive ? t('dealerDashboard.inventory.status.active', 'active') : 'inactive'}
                    </span>
                    <span className="text-xs text-[#717182]">{vehicle.leads ?? 0} {t('dealerDashboard.inventory.leads')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div> */}
      </div>
    </div>
    </>
  );
}